import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { configApiRef, useApi } from '@backstage/core-plugin-api';
import { ButtonIcon } from '@backstage/ui';
import { RiExpandDiagonalLine, RiSubtractLine } from '@remixicon/react';
import { BotIconComponent as MCPChatIcon } from '../../assets/Icons';
import type { MCPServer } from '../../types';
import { ExpertPanel } from '../ExpertPanel/ExpertPanel';
import { ExpertToolsButton } from '../ExpertPanel/ExpertToolsButton';
import styles from './ExpertButton.module.css';

export const ExpertButton = () => {
  const navigate = useNavigate();
  const configApi = useApi(configApiRef);
  const streamingEnabled =
    configApi.getOptionalBoolean('mcpChat.streaming.enabled') ?? true;
  const { pathname, search } = useLocation();
  const [open, setOpen] = useState(false);
  const [hasOpened, setHasOpened] = useState(false);
  const [mcpServers, setMcpServers] = useState<MCPServer[]>([]);
  const [pendingPrompt, setPendingPrompt] = useState<string>();
  const cancelPendingRequestRef = useRef<(() => void) | undefined>(undefined);
  const pendingNavigationRef = useRef<string>();
  const conversationIdRef = useRef<string>();

  const handleConversationUpdated = (updatedConversationId: string) => {
    conversationIdRef.current = updatedConversationId;
  };

  const navigateToFullPage = (promptToResume?: string) => {
    const savedConversationId = conversationIdRef.current;
    const path = savedConversationId
      ? `/xpert?conversationId=${encodeURIComponent(savedConversationId)}`
      : '/xpert';
    if (promptToResume) {
      navigate(path, { state: { pendingPrompt: promptToResume } });
    } else {
      navigate(path);
    }
  };

  const handlePendingPromptChange = (
    prompt: string | undefined,
    cancelRequest?: () => void,
  ) => {
    setPendingPrompt(prompt);
    cancelPendingRequestRef.current = prompt ? cancelRequest : undefined;
    if (!prompt && pendingNavigationRef.current) {
      const promptToResume = pendingNavigationRef.current;
      pendingNavigationRef.current = undefined;
      navigateToFullPage(
        conversationIdRef.current ? undefined : promptToResume,
      );
    }
  };

  const closePanel = () => {
    setOpen(false);
  };

  const openAskXpert = () => {
    const promptToResume = pendingPrompt;
    closePanel();
    if (promptToResume && !streamingEnabled) {
      pendingNavigationRef.current = promptToResume;
      return;
    }
    if (promptToResume) {
      cancelPendingRequestRef.current?.();
    }
    navigateToFullPage(promptToResume);
  };

  useEffect(() => {
    const root = document.documentElement;
    const previousOffset = root.style.getPropertyValue(
      '--xlrt-support-button-bottom',
    );
    root.style.setProperty('--xlrt-support-button-bottom', '5.5rem');
    return () => {
      if (previousOffset) {
        root.style.setProperty('--xlrt-support-button-bottom', previousOffset);
      } else {
        root.style.removeProperty('--xlrt-support-button-bottom');
      }
    };
  }, []);

  useEffect(() => {
    if (!open) return undefined;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !event.defaultPrevented) {
        closePanel();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open]);

  useEffect(() => {
    pendingNavigationRef.current = undefined;
    setOpen(false);
  }, [pathname, search]);

  return (
    <>
      <ButtonIcon
        className={styles.button}
        icon={
          <span className={styles.buttonIcon}>
            <MCPChatIcon fontSize="medium" />
          </span>
        }
        aria-label="Ask expert"
        aria-expanded={open}
        onPress={() => {
          if (!open) pendingNavigationRef.current = undefined;
          setHasOpened(true);
          setOpen(currentOpen => !currentOpen);
        }}
      />
      {hasOpened && (
        <aside
          className={`${styles.panel} ${!open ? styles.panelHidden : ''}`}
          aria-label="xpert"
          role="dialog"
          hidden={!open}
        >
          <div className={styles.panelHeader}>
            <span>xpert</span>
            <div className={styles.panelActions}>
              <ExpertToolsButton
                className={styles.headerButton}
                mcpServers={mcpServers}
              />
              <ButtonIcon
                className={styles.headerButton}
                icon={<RiSubtractLine size={16} />}
                aria-label="Minimize xpert"
                onPress={closePanel}
              />
              <ButtonIcon
                className={styles.headerButton}
                icon={<RiExpandDiagonalLine size={16} />}
                aria-label="Open Ask xpert"
                onPress={openAskXpert}
              />
            </div>
          </div>
          <div className={styles.panelContent}>
            <ExpertPanel
              onConversationUpdated={handleConversationUpdated}
              onMcpServersChange={setMcpServers}
              onPendingPromptChange={handlePendingPromptChange}
            />
          </div>
        </aside>
      )}
    </>
  );
};
